/**
 * Minimal RFC-4180 CSV tokenizer.
 *
 * Why not a dependency:
 *   The import edge function runs on Supabase Edge (Deno). Pulling
 *   `papaparse` or `csv-parse` increases cold-start time and adds a
 *   moving piece to the dependency surface. Our needs are tiny — we
 *   just need RFC-4180 quote handling, embedded newlines, and
 *   delimiter sniffing. Implementing it here keeps the helper
 *   shared between the browser-side preview and the edge worker
 *   with zero adapters.
 *
 * Handles:
 *   - Quoted fields containing the delimiter, quotes (doubled), and
 *     literal newlines.
 *   - Trailing newline (with or without).
 *   - CRLF and LF line endings (mixed within a file is fine).
 *   - Empty lines between rows (preserved as empty rows so row
 *     numbers stay aligned to the source file).
 *
 * Does NOT handle:
 *   - BOM stripping — caller is expected to strip a leading U+FEFF.
 *   - Encoding detection — caller passes already-decoded text.
 *   - Schema/validation — that's the column-mapper's job.
 */

export interface TokenizeOptions {
  delimiter?: ',' | ';' | '\t' | '|';
}

/**
 * Sniff the most-likely single-character delimiter from a header
 * line. Falls back to comma when ambiguous. Only looks outside
 * quotes.
 */
export function sniffDelimiter(headerLine: string): ',' | ';' | '\t' | '|' {
  const candidates: Array<',' | ';' | '\t' | '|'> = [',', '\t', ';', '|'];
  let best: ',' | ';' | '\t' | '|' = ',';
  let bestCount = -1;
  for (const c of candidates) {
    let count = 0;
    let inQuotes = false;
    for (let i = 0; i < headerLine.length; i++) {
      const ch = headerLine[i];
      if (ch === '"') {
        if (inQuotes && headerLine[i + 1] === '"') {
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (!inQuotes && ch === c) {
        count++;
      }
    }
    if (count > bestCount) {
      best = c;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Tokenize a full CSV string into rows of string cells. Row numbers
 * in the output are aligned to the source file's 1-based line index
 * of the row's FIRST line (multi-line quoted cells count as one
 * row, anchored to the line they started on).
 */
export function tokenizeCsv(
  text: string,
  options: TokenizeOptions = {},
): Array<{ rowNumber: number; cells: string[] }> {
  const stripped = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const delimiter = options.delimiter ?? sniffDelimiter(stripped.split(/\r?\n/, 1)[0] ?? '');

  const rows: Array<{ rowNumber: number; cells: string[] }> = [];
  let cell = '';
  let row: string[] = [];
  let inQuotes = false;
  let physicalLine = 1;
  let rowStartLine = 1;
  let rowHasContent = false;

  const flushCell = () => {
    row.push(cell);
    cell = '';
  };
  const flushRow = () => {
    flushCell();
    rows.push({ rowNumber: rowStartLine, cells: row });
    row = [];
    rowHasContent = false;
    rowStartLine = physicalLine;
  };

  for (let i = 0; i < stripped.length; i++) {
    const ch = stripped[i];

    if (inQuotes) {
      if (ch === '"') {
        if (stripped[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        if (ch === '\n') physicalLine++;
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      rowHasContent = true;
      continue;
    }
    if (ch === delimiter) {
      flushCell();
      rowHasContent = true;
      continue;
    }
    if (ch === '\r') {
      // swallow — newline handler below does the work
      continue;
    }
    if (ch === '\n') {
      flushRow();
      physicalLine++;
      continue;
    }
    rowHasContent = true;
    cell += ch;
  }

  // Trailing cell / row (file did not end on a newline)
  if (cell.length > 0 || row.length > 0 || rowHasContent) {
    flushRow();
  }

  return rows;
}
